import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
    AppBar, 
    Avatar, 
    // Badge, 
    Box, 
    InputBase, 
    IconButton, 
    // Menu, 
    // MenuItem, 
    Toolbar, 
    Typography,
    Tabs,
    Tab,
    // Link,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { 
    Search, 
    // Notifications
} from "@mui/icons-material";
import getlistssmall from "../assets/images/logoDark.png";
import { getUserById } from "../services/userService";
import { pictureUrlToString } from "../utils/pictureUrl";

const SearchAppBar = () => {
    const location = useLocation();
    const navigate = useNavigate();    
    // State variables
    const [searchText, setSearchText] = useState("");

    const [anchorEl, setAnchorEl] = useState(null);
    const [searchTab, setSearchTab] = useState(0); // 0: AI Search, 1: Build your Search, 2: Persona Search
    
    const userInfo = typeof window !== 'undefined' ? localStorage.getItem('userInfo') : null;
    const user = userInfo ? JSON.parse(userInfo) : null;
    
    // State for updated user data - only update credits, not the entire user object
    const [remainingCredits, setRemainingCredits] = useState(user?.remainingCredits || 0);
    
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const queryText = params.get("query") || "";
        setSearchText(queryText);
        if (!userInfo) {
            navigate('/login');
        }
    }, [userInfo, navigate, location.search]);

    // Fetch user data every 5 seconds - only update credits
    useEffect(() => {
        if (!user || !user._id) return;
        const fetchUserData = async () => {
            try {
                const userData = await getUserById(user._id);
                setRemainingCredits(userData.remainingCredits);
                localStorage.setItem('userInfo', JSON.stringify(userData));
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };
        fetchUserData();
        const interval = setInterval(fetchUserData, 5000);
        return () => clearInterval(interval);
    }, [user?._id]);

    // Event handlers
    const handleProfileMenu = useCallback((event) => {
        navigate('/account');
    }, [navigate]);

    // Memoize the credits display to prevent unnecessary re-renders
    const creditsDisplay = useMemo(() => (
        <Box display="flex" alignItems="center" padding={1} margin={1} sx={{ px: 2, bgcolor: "#232323", color: "#fff", fontWeight: 100, borderRadius: 2, fontSize: "1rem", }}>
            <Box component="span" sx={{ color: "#fff", fontWeight: 100, ml: 1 }}>Credits : </Box>
            <Box component="span" sx={{ color: "#1BE885", fontWeight: 100, mx: 0.5 }}>{remainingCredits} </Box>
            {/* <Box component="span" sx={{ color: "#fff", fontWeight: 100 }}>/ {user.totalCredits}</Box> */}
        </Box>
    ), [remainingCredits]);

    // Memoize the profile display
    const profileDisplay = useMemo(() => (
        <Box display="flex" alignItems="center" sx={{ mx: 0.5, cursor: "pointer" }} onClick={handleProfileMenu}>
            <Avatar src={pictureUrlToString(user?.profilePicture)} sx={{ width: 36, height: 36, mr: 0.5 }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{user?.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1).toLowerCase() : ''}</Typography>
        </Box>
    ), [user?.profilePicture, user?.firstName, handleProfileMenu]);

    const handleSearch = useCallback((e) => {
        e.preventDefault();
        if (searchText.trim()) {
            const params = new URLSearchParams();
            params.set("query", searchText);
            params.set("page", 1);
            // params.set("type", getSearchType());
            navigate(`/search?${params.toString()}`);
        }
    }, [searchText, navigate]);

    const handleTabChange = useCallback((event, newValue) => {
        setSearchTab(newValue);
    }, []);

    const handleSearchTextChange = useCallback((e) => {
        setSearchText(e.target.value);
    }, []);

    return (
        <AppBar position="sticky" elevation={2} sx={{ bgcolor: "black", color: grey[500], boxShadow: 1, minHeight: 75, maxHeight: 75, padding: 0}}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', pt:1 }}>
                {/* Centered Search with Tabs */}
                <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1} sx={{ flex: 1, ml: { xs: 2, md: 0 } }}>
                    {/* Search Bar */}
                    <Box sx={{ bgcolor: "#fff", borderRadius: 1, px: 2, my: 1, minWidth: { xs: 280, md: 640 }, display: "flex", alignItems: "center" }} >
                        <form style={{ display: "flex", width: "100%" }} onSubmit={handleSearch}>            
                            <InputBase 
                                placeholder='Search with getList{a} Search...' 
                                sx={{ color: "#000", width: "100%", fontSize: "1rem" }} 
                                value={searchText} 
                                onChange={handleSearchTextChange} 
                            />
                            <IconButton sx={{ color: '#000', ml: 1 }} aria-label="search" type="submit" >
                                <Search />
                            </IconButton>
                        </form>
                    </Box>
                </Box>
                {/* Search Bar */}

                {/* Credits */}
                <Box display="flex" alignItems="center" gap={2} sx={{ marginLeft: "auto" }}>
                    {creditsDisplay}
                    {profileDisplay}
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default SearchAppBar;